import React, { useEffect, useState } from "react";
import { ActivityIndicator, Keyboard, StyleSheet, View } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import Text from "@components/AppText";
import Button from "@components/Button";
import HeaderWithBackAndTitle from "@components/HeaderWithBackAndTitle";
import { ACCOUNT_EDITED } from "@constants/analyticsEvents";
import colors from "@constants/color";
import t from "@constants/localization";
import analytics from "@services/analytics";
import { getWalletOtp, validateUpi } from "@services/api";
import { FloatingLabelInput } from "../../components/FloatingLabelInput";
import { STATE_STATUS } from "../../reducers/actionTypes";
import { actions as walletActions } from "../../reducers/oldWalletReducer";
import { scale, verticalScale } from "../../utils/scale";

const PATH = "screens/Wallet/AddNewUPI";

const ACCOUNT_TYPE = {
  bank_account: "bank_account",
  vpa: "vpa",
};

const AddNewUpi = ({ route, navigation }) => {
  const { params } = route;
  const [value, setValue] = useState(
    params && params.edit ? params.data.upi_address : null,
  );
  const wallet = useSelector((state) => state.wallet);
  const [onVerifySubmitted, setOnVerifySubmitted] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [vpaDetails, setVpaDetails] = useState();
  const [errMessage, setErrMessage] = useState(null);
  const [isNewAction, setIsNewAction] = useState(false);
  const handleTextChange = (newText) => {
    setValue(newText);
  };
  const dispatch = useDispatch();
  const IS_EDIT = params && params.edit;
  const EDIT_DATA = params && params.data;

  const validateVpa = () => {
    Keyboard.dismiss();
    setOnVerifySubmitted(true);
    validateUpi({ upi_address: value.trim() })
      .then((response) => {
        setOnVerifySubmitted(false);
        // setIsVerified(true);
        if (response.data.success === true) {
          setErrMessage(null);
          setIsVerified(true);
          const customer_name = response.data.customer_name
            .replace(/\s\s+/g, " ")
            .toLowerCase()
            .split(" ")
            .map((el) => el[0].toUpperCase() + el.substring(1))
            .join(" ");
          setVpaDetails(customer_name);
        } else {
          setIsVerified(false);
          setErrMessage(t("gullak/wrongUpi"));
        }
      })
      .catch((e) => {
        setIsVerified(false);
        setOnVerifySubmitted(false);
        setErrMessage(t("gullak/wrongUpi"));
      });
  };

  useEffect(() => {
    setIsVerified(false);
    setVpaDetails(null);
    setErrMessage(null);
    if (value && value.includes("@")) {
      const [a, b] = value.split("@");
      if (a.length >= 3 && b.length >= 3) {
        setDisabled(false);
      } else {
        setDisabled(true);
      }
    } else {
      setDisabled(true);
    }
  }, [value]);

  useEffect(() => {
    if (wallet.createFundAccountStatus === STATE_STATUS.CREATED) {
      const item = {
        ...wallet.createFundAccountData.createAccountResult,
        ...{ account_type: "vpa" },
      };
      dispatch(walletActions.fetchFundAccounts());
      dispatch(walletActions.resetFundAccountBank());
      navigation.navigate("WithdrawMoney");
    } else if (wallet.createFundAccountStatus === STATE_STATUS.CREATE_FAILED) {
      if (
        wallet.createFundAccountError &&
        wallet.createFundAccountError.error &&
        wallet.createFundAccountError.error === "INVALID_OTP"
      ) {
        // do nothing
      } else {
        if (isNewAction) navigation.navigate("WithdrawMoney");
      }
    }
  }, [wallet.createFundAccountStatus]);

  const onSave = async () => {
    if (IS_EDIT) {
      await analytics.trackEvent(ACCOUNT_EDITED, {
        method: "upi",
      });
      dispatch(
        walletActions.createFundAccountBank({
          edit: true,
          fund_account_id: EDIT_DATA.id,
          account_type: ACCOUNT_TYPE.vpa,
          upi_address: value,
        }),
      );
    } else {
      sendOtpToUser();
    }
  };

  const sendOtpToUser = () => {
    const vpaPayload = {
      account_type: ACCOUNT_TYPE.vpa,
      upi_address: value,
      name: vpaDetails,
    };
    getWalletOtp({ type: "create", isDebug: __DEV__, ...vpaPayload })
      .then((res) => {
        setIsNewAction(true);
        navigation.navigate("VerifyAccount", {
          type: "create",
          item: {
            ...vpaPayload,
          },
        });
      })
      .catch((err) => {
        console.log("error", err.response.data);
      });
  };

  return (
    <View>
      <HeaderWithBackAndTitle
        headertitle={t("gullak/addNewUpi")}
        headerColor="#fff"
        hideShadow={false}
        navigation={navigation}
        route={route}
        headerStyle={{
          fontFamily: "NotoSansDevanagari-Bold",
          fontSize: scale(18),
        }}
      />
      <View
        style={{ width: "100%", height: "90%", paddingHorizontal: scale(15) }}
      >
        <Text style={{ fontSize: scale(14), marginTop: verticalScale(33) }}>
          {t(`${PATH}/enterUPIheading`)}
        </Text>
        <View
          style={{
            marginTop: verticalScale(30),
            position: "relative",
          }}
        >
          <FloatingLabelInput
            isVerified={isVerified}
            label={t(`${PATH}/enterUPI`)}
            value={value}
            onChangeText={handleTextChange}
          />

          {isVerified && vpaDetails && (
            <View>
              <Text
                style={{
                  fontSize: 14,
                  color: "rgba(0,0,0,0.56)",
                  marginTop: 10,
                }}
              >
                {t("gullak/registeredName")}
              </Text>
              <Text bold style={{ fontSize: 16 }}>
                {vpaDetails}
              </Text>
            </View>
          )}
          {errMessage && (
            <Text
              bold
              style={{
                color: colors.internationalOrange,
                fontSize: 16,
                position: "absolute",
                top: 50,
              }}
            >
              {errMessage}
            </Text>
          )}
        </View>
        {!isVerified && (
          <View>
            {onVerifySubmitted ? (
              <View
                style={{ alignSelf: "flex-end", marginTop: verticalScale(20) }}
              >
                <ActivityIndicator size="large" color={colors.primary} />
              </View>
            ) : (
              <Button
                buttonWidth="40%"
                btnText={t(`${PATH}/verify`)}
                disabled={disabled}
                buttonFontType="head1"
                // height={40}
                borderRadius={10}
                onPress={() => validateVpa()}
                containerStyle={{
                  marginTop: verticalScale(20),
                  alignSelf: "flex-end",
                }}
              />
            )}
          </View>
        )}
      </View>
      {isVerified && (
        <View
          style={{
            padding: scale(15),
            width: "100%",
            position: "absolute",
            bottom: 10,
          }}
        >
          <Button
            buttonWidth="100%"
            btnText={t(`${PATH}/save`)}
            disabled={false}
            buttonFontType="head1"
            height={50}
            borderRadius={10}
            onPress={() => onSave()}
            containerStyle={{
              alignSelf: "center",
              width: "100%",
              marginTop: verticalScale(20),
              alignSelf: "flex-end",
            }}
          />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  beLikeRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  headerContainer: {
    paddingTop: 20,
    paddingBottom: 10,
    paddingHorizontal: 15,
  },
  text: {
    color: colors.white,
  },
});

export default AddNewUpi;
